import type { LevelEnterPayload, RunEndPayload, RunEndStats, RunMode } from "../../shared/telemetry";
import type { Game } from "../engine/game";
import { guarded, send, type TelemetryContext } from "./telemetry";

function durationSecondsSince(startedAt: unknown): number {
	const startedMs = typeof startedAt === "string" ? Date.parse(startedAt) : NaN;
	if (Number.isNaN(startedMs)) return 0;
	return Math.max(0, Math.floor((Date.now() - startedMs) / 1000));
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function modeOf(game: Game): RunMode {
	return game.isTutorial ? "tutorial" : "normal";
}

function buildStatsSnapshot(game: Game): RunEndStats {
	return {
		...(game.stats.tracker.serialize() as Omit<RunEndStats, "turnsPerLevel">),
		turnsPerLevel: Object.fromEntries(game.stats.turnsPerLevel),
	};
}

export function buildRunContext(game: Game): TelemetryContext | null {
	if (game.player === null || game.player.hp <= 0) return null;
	return {
		runId: game.runId,
		level: game.currentLevelNumber,
		turn: game.getCurrentBoard().turnCount,
		mode: modeOf(game),
		stats: buildStatsSnapshot(game),
	};
}

export function buildLevelEnterPayload(game: Game): LevelEnterPayload {
	return {
		runId: game.runId,
		level: game.currentLevelNumber,
		turn: game.getCurrentBoard().turnCount,
		hp: game.player?.hp ?? 0,
		mode: modeOf(game),
		stats: buildStatsSnapshot(game),
	};
}

export function buildRunEndPayload(game: Game, outcome: RunEndPayload["outcome"]): RunEndPayload {
	return {
		runId: game.runId,
		outcome,
		seed: game.seed,
		endedOnLevel: game.currentLevelNumber,
		totalTurns: game.stats.getTotalTurnsElapsed(),
		durationSeconds: durationSecondsSince(game.stats.startedAt),
		causeOfDeath: game.stats.causeOfDeath,
		mode: modeOf(game),
		character: game.characterId,
		gameWon: game.gameFlags.gameWon,
		stats: buildStatsSnapshot(game),
	};
}

export function buildRunEndPayloadFromSave(saveJson: string): RunEndPayload | null {
	try {
		const data: unknown = JSON.parse(saveJson);
		if (!isRecord(data)) return null;
		const { runId, seed, currentLevelNumber, stats } = data;
		if (typeof runId !== "string" || typeof seed !== "string" || typeof currentLevelNumber !== "number") return null;
		if (!isRecord(stats) || !isRecord(stats.tracker)) return null;
		const turnsPerLevelRaw = isRecord(stats.turnsPerLevel) ? stats.turnsPerLevel : {};
		const turnsPerLevel: Record<number, number> = {};
		let totalTurns = 0;
		for (const [level, turns] of Object.entries(turnsPerLevelRaw)) {
			if (typeof turns === "number") {
				turnsPerLevel[Number(level)] = turns;
				totalTurns += turns;
			}
		}
		return {
			runId,
			outcome: "abandoned",
			seed,
			endedOnLevel: currentLevelNumber,
			totalTurns,
			durationSeconds: durationSecondsSince(stats.startedAt),
			causeOfDeath: (stats.causeOfDeath as RunEndPayload["causeOfDeath"]) ?? null,
			mode: data.isTutorial === true ? "tutorial" : "normal",
			character: typeof data.characterId === "string" ? data.characterId : null,
			gameWon: isRecord(data.gameFlags) && data.gameFlags.gameWon === true,
			stats: {
				...(stats.tracker as Omit<RunEndStats, "turnsPerLevel">),
				turnsPerLevel,
			},
		};
	} catch {
		return null;
	}
}

export const trackRunStart = guarded((game: Game): void => {
	send("run_start", { runId: game.runId, seed: game.seed, mode: modeOf(game), character: game.characterId });
	send("level_enter", buildLevelEnterPayload(game));
});

export const trackLevelEnter = guarded((game: Game): void => {
	send("level_enter", buildLevelEnterPayload(game));
});

export const trackRunSuspendQuit = guarded((game: Game): void => {
	const context = buildRunContext(game);
	if (context === null) return;
	send("run_suspend", { ...context, reason: "quit" });
});

export const trackRunResume = guarded((game: Game): void => {
	const context = buildRunContext(game);
	if (context === null) return;
	const { runId, level, turn, mode } = context;
	send("run_resume", { runId, level, turn, mode });
});

export const trackRunEnd = guarded((game: Game, outcome: RunEndPayload["outcome"]): void => {
	send("run_end", buildRunEndPayload(game, outcome));
});

export const trackRunAbandoned = guarded((saveJson: string): void => {
	const payload = buildRunEndPayloadFromSave(saveJson);
	if (payload !== null) send("run_end", payload);
});
